/**
 * TRAC-848: formatting shared by every export renderer, so a tire reads the same
 * in Markdown, HTML, PDF, and CSV.
 */

import type { ChatExport, ExportSpeaker, TireSnapshot } from './exportModel';

/** What the customer sees as the name of each participant. */
export const SPEAKER_LABELS: Record<ExportSpeaker, string> = {
  customer: 'You',
  traci: 'AI Assist',
  'human-agent': 'Tire Rack specialist',
  system: 'Tire Rack',
};

export const formatDateTime = (d: Date): string =>
  d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

export const formatDate = (d: Date): string =>
  d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

export const formatTime = (d: Date): string =>
  d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

/** "Michelin CrossClimate2", falling back gracefully when a field is missing. */
export const tireName = (tire: TireSnapshot): string =>
  [tire.manufacturer, tire.modelName].filter(Boolean).join(' ') || 'Tire';

/**
 * `TireInfo.overallRating` is stored on a 10-point scale; the tire card shows it
 * halved next to five stars (`MessageRenderer.tsx:566` renders
 * `(overallRating / 2).toFixed(1)`). Export the number the customer actually
 * saw — printing the raw value as "out of 5" would turn a 9.2 into "9.2 out of 5".
 *
 * Note this is a DIFFERENT scale convention from a `rating_display` card, which
 * shows its score raw, out of 10.
 */
export const tireRatingOutOfFive = (tire: TireSnapshot): number | null =>
  typeof tire.overallRating === 'number' ? tire.overallRating / 2 : null;

/** "4.6 out of 5 (1,234 reviews)" — omitted entirely when there is no rating. */
export const ratingLine = (tire: TireSnapshot): string | null => {
  const outOfFive = tireRatingOutOfFive(tire);
  if (outOfFive === null) return null;
  const reviews =
    typeof tire.reviewCount === 'number'
      ? ` (${tire.reviewCount.toLocaleString()} review${tire.reviewCount === 1 ? '' : 's'})`
      : '';
  return `${outOfFive.toFixed(1)} out of 5${reviews}`;
};

/**
 * The "as of" sentence. Prices are a snapshot taken when each tire was shown, so
 * the honest stamp is the window the conversation spanned — collapsed to a single
 * date when it all happened on one day.
 */
export const priceAsOfSentence = (chat: ChatExport): string | null => {
  if (!chat.priceWindow) return null;
  const { earliest, latest } = chat.priceWindow;
  const sameDay = earliest.toDateString() === latest.toDateString();
  return sameDay
    ? `Prices shown as of ${formatDate(earliest)}. They may have changed since.`
    : `Prices shown as of when each tire appeared, between ${formatDate(earliest)} and ${formatDate(latest)}. They may have changed since.`;
};

/** Absolute URL for a link that may be site-relative, so exported links work off-site. */
export const absoluteUrl = (url: string, origin = 'https://www.tirerack.com'): string => {
  if (/^https?:\/\//i.test(url)) return url;
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

/** Filename stem, e.g. `tirerack-chat-2026-07-28`. */
export const exportFilename = (chat: ChatExport, extension: string): string => {
  const d = chat.exportedAt;
  const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `tirerack-chat-${stamp}.${extension}`;
};

/** The warning shown at the top of an export we know is incomplete. */
export const completenessWarning = (chat: ChatExport): string | null =>
  chat.completeness.status === 'complete'
    ? null
    : `Heads up: this copy may be missing part of your conversation. ${chat.completeness.reasons.join(' ')}`;
